import React, { useEffect, useMemo, useState } from "react";
import MerchantPageShell from "../../components/merchant/MerchantPageShell";
import {
  bulkDeleteProducts,
  deleteProduct,
  getProductTryOnDiagnostics,
  getProducts,
  updateProductActive,
  updateProductTryOn,
  recomputeProductMask,
} from "../../lib/catalogApi";
import { useNavigate } from "react-router-dom";
import { normalizeBrowseCategory } from "../../catalog/meshResolver";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

const FIT_OPTIONS_BY_CATEGORY = {
  top: ["fitted", "relaxed", "oversized"],
  bottom: ["fitted", "relaxed", "wide"],
  outer: ["closed"],
  dress: [],
};

const VARIANT_OPTIONS_BY_CATEGORY = {
  dress: ["mini", "midi", "maxi"],
  bottom: ["shorts_mini", "shorts", "bermuda"],
};

function getTryOnState(product) {
  const tryOn = product?.try_on || product?.tryOn || null;
  const assets = tryOn?.assets || {};

  if (!tryOn) return "not processed";
  if (assets.front || assets.back) return "ready";
  if (tryOn.status === "processing") return "processing";
  if (tryOn.status === "failed") return "failed";
  return tryOn.status || "unknown";
}

function getProductImage(product) {
  return product?.image_url || product?.imageUrl || null;
}

function getProductIssues(product) {
  const issues = [];

  if (!getProductImage(product)) issues.push("Missing image");
  if (!product?.product_url) issues.push("Missing product URL");
  if (!product?.category) issues.push("Missing category");
  if (!product?.price_amount) issues.push("Missing price");

  return issues;
}

function getTryOnAvailability(product) {
  const tryOn = product?.try_on || product?.tryOn || null;
  const assets = tryOn?.assets || {};

  if (assets.front || assets.back) {
    return {
      label: "Available for try on",
      tone: "available",
    };
  }

  return {
    label: "Unavailable for try on",
    tone: "unavailable",
  };
}

function isLaunchReady(product) {
  return getProductIssues(product).length === 0;
}

export default function MerchantCatalogManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [deleting, setDeleting] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [togglingActiveId, setTogglingActiveId] = useState(null);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkUnpublishing, setBulkUnpublishing] = useState(false);

  const [debugById, setDebugById] = useState({});
  const [loadingDebugId, setLoadingDebugId] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showOnlyNeedsAttention, setShowOnlyNeedsAttention] = useState(false);
  const [recomputingId, setRecomputingId] = useState(null);
  const [pendingRecomputeId, setPendingRecomputeId] = useState(null);

  const navigate = useNavigate();

  async function loadProducts() {
    setLoading(true);
    try {
      const data = await getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load products", err);
      alert("Could not load products.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    if (!showOnlyNeedsAttention) return products;
    return products.filter((product) => getProductIssues(product).length > 0);
  }, [products, showOnlyNeedsAttention]);

  const allVisibleIds = useMemo(
    () => filteredProducts.map((p) => p.id),
    [filteredProducts]
  );

  const allSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.includes(id));

  const selectedProducts = useMemo(
    () => products.filter((p) => selectedIds.includes(p.id)),
    [products, selectedIds]
  );

  const selectedActiveCount = useMemo(
    () => selectedProducts.filter((p) => !!p.is_active).length,
    [selectedProducts]
  );

  const selectedInactiveCount = useMemo(
    () => selectedProducts.filter((p) => !p.is_active).length,
    [selectedProducts]
  );

  const summary = useMemo(() => {
    const total = products.length;
    const needsAttention = products.filter((p) => getProductIssues(p).length > 0).length;
    const readyForLaunch = products.filter((p) => isLaunchReady(p)).length;
    const publicCount = products.filter((p) => !!p.is_active).length;
    const tryOnAvailableCount = products.filter(
      (p) => getTryOnAvailability(p).tone === "available"
    ).length;

    return {
      total,
      needsAttention,
      readyForLaunch,
      publicCount,
      tryOnAvailableCount,
    };
  }, [products]);

  function toggleSelected(productId) {
    setSelectedIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allVisibleIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => Array.from(new Set([...prev, ...allVisibleIds])));
  }

  async function handleDelete(productId) {
    if (!window.confirm("Delete this product?")) return;

    try {
      setDeleting(productId);
      await deleteProduct(productId);

      setProducts((prev) => prev.filter((p) => p.id !== productId));
      setSelectedIds((prev) => prev.filter((id) => id !== productId));
      setDebugById((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    } catch (err) {
      console.error("Failed to delete product", err);
      alert("Could not delete product.");
    } finally {
      setDeleting(null);
    }
  }

  async function handleBulkDelete() {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected product(s)?`)) return;

    try {
      setBulkDeleting(true);
      await bulkDeleteProducts(selectedIds);

      setProducts((prev) => prev.filter((p) => !selectedIds.includes(p.id)));
      setDebugById((prev) => {
        const next = { ...prev };
        selectedIds.forEach((id) => delete next[id]);
        return next;
      });
      setSelectedIds([]);
    } catch (err) {
      console.error("Failed to bulk delete products", err);
      alert("Could not delete selected products.");
    } finally {
      setBulkDeleting(false);
    }
  }

  async function handleToggleActive(product) {
    const nextIsActive = !product.is_active;

    try {
      setTogglingActiveId(product.id);
      const updated = await updateProductActive(product.id, nextIsActive);

      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? updated : p))
      );
    } catch (err) {
      console.error("Failed to update product visibility", err);
      alert("Could not update product visibility.");
    } finally {
      setTogglingActiveId(null);
    }
  }

  async function handleBulkSetActive(nextIsActive) {
    if (!selectedIds.length) return;

    const targetProducts = products.filter((p) => selectedIds.includes(p.id));
    const targetIds = targetProducts
      .filter((p) => Boolean(p.is_active) !== Boolean(nextIsActive))
      .map((p) => p.id);

    if (!targetIds.length) return;

    const actionWord = nextIsActive ? "publish" : "unpublish";
    if (
      !window.confirm(
        `${actionWord.charAt(0).toUpperCase() + actionWord.slice(1)} ${targetIds.length} selected product(s)?`
      )
    ) {
      return;
    }

    try {
      if (nextIsActive) setBulkPublishing(true);
      else setBulkUnpublishing(true);

      const updatedPairs = await Promise.all(
        targetIds.map(async (productId) => {
          const updated = await updateProductActive(productId, nextIsActive);
          return [productId, updated];
        })
      );

      const updatedMap = new Map(updatedPairs);

      setProducts((prev) =>
        prev.map((product) => updatedMap.get(product.id) || product)
      );
    } catch (err) {
      console.error(
        `Failed to ${nextIsActive ? "publish" : "unpublish"} selected products`,
        err
      );
      alert(`Could not ${nextIsActive ? "publish" : "unpublish"} selected products.`);
    } finally {
      if (nextIsActive) setBulkPublishing(false);
      else setBulkUnpublishing(false);
    }
  }

  async function handleOverrideChange(productId, updates) {
    try {
      const updated = await updateProductTryOn(productId, updates);

      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? updated : p))
      );
    } catch (err) {
      console.error("Failed to update try-on override", err);
      alert("Could not update override.");
    }
  }

async function handleRecompute(productId) {
  try {
    setRecomputingId(productId);

    const updated = await recomputeProductMask(productId);

    setProducts((prev) =>
      prev.map((p) => (p.id === productId ? updated : p))
    );

    setDebugById((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });

    setPendingRecomputeId(null);
  } catch (err) {
    console.error("Failed to recompute mask", err);
    alert("Could not recompute mask.");
  } finally {
    setRecomputingId(null);
  }
}

  async function toggleDebug(productId) {
    if (debugById[productId]) {
      setDebugById((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      return;
    }

    try {
      setLoadingDebugId(productId);
      const data = await getProductTryOnDiagnostics(productId);
      setDebugById((prev) => ({
        ...prev,
        [productId]: data,
      }));
    } catch (err) {
      console.error("Failed to load try-on diagnostics", err);
      alert("Could not load try-on diagnostics.");
    } finally {
      setLoadingDebugId(null);
    }
  }

  return (
    <MerchantPageShell
      eyebrow="Catalog"
      title="Manage products"
      lede="Import products, review catalog readiness, control public visibility, and manage try-on setup from one place."
    >
      <style>{`
        .merchant-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        .merchant-summary-card {
          padding: 18px 20px !important;
          min-height: auto !important;
        }

        .merchant-summary-value {
          font-size: 16px;
          color: rgba(40,40,40,0.72);
          margin-top: 8px;
        }

        .merchant-readiness-overview {
          display: grid;
          grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .merchant-readiness-copy {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .merchant-readiness-metrics {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .merchant-readiness-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          font-size: 14px;
          color: rgba(40,40,40,0.8);
        }

        .merchant-readiness-row strong {
          color: rgba(40,40,40,0.92);
          font-weight: 600;
        }

        .merchant-catalog-toolbar {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 20px;
        }

        .merchant-catalog-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }

        .merchant-product-card {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-height: 100%;
          padding: 12px !important;
          border-radius: 22px !important;
        }

        .merchant-product-thumb-wrap {
          position: relative;
          aspect-ratio: 0.78;
          border-radius: 18px;
          overflow: hidden;
          background: rgba(0,0,0,0.05);
          margin-bottom: 6px;
        }

        .merchant-product-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.18s ease;
        }

        .merchant-product-thumb-wrap:hover .merchant-product-thumb {
          transform: scale(1.015);
        }

        .merchant-product-select {
          position: absolute;
          top: 12px;
          left: 12px;
          width: 18px;
          height: 18px;
          z-index: 2;
        }

        .merchant-product-status-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 2;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          color: white;
          letter-spacing: 0.01em;
        }

        .merchant-product-status-badge.published {
          background: rgba(50, 120, 72, 0.92);
        }

        .merchant-product-status-badge.hidden {
          background: rgba(20, 20, 20, 0.72);
        }

        .merchant-product-meta {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .merchant-status-block {
          margin-top: 2px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .merchant-status-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          font-size: 13px;
          color: rgba(40,40,40,0.78);
        }

        .merchant-status-label {
          color: rgba(40,40,40,0.58);
        }

        .merchant-status-value {
          font-weight: 600;
          color: rgba(40,40,40,0.9);
          text-align: right;
        }

        .merchant-status-value.available {
          color: #2f6f45;
        }

        .merchant-status-value.unavailable {
          color: #8a5a2b;
        }

        .merchant-product-actions {
          display: flex;
          gap: 0;
          flex-wrap: wrap;
          margin-top: auto;
          align-items: center;
          padding-top: 4px;
        }

        .merchant-product-debug {
          margin-top: 10px;
          padding: 12px;
          border-radius: 12px;
          background: rgba(0,0,0,0.04);
        }

        .merchant-readiness-summary {
          margin-top: 10px;
          padding: 12px 14px;
          border-radius: 16px;
          background: rgba(0,0,0,0.035);
        }

        .merchant-readiness-summary summary {
          cursor: pointer;
          list-style: none;
        }

        .merchant-readiness-summary summary::-webkit-details-marker {
          display: none;
        }

        .merchant-readiness-summary-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(40,40,40,0.84);
        }

        .merchant-readiness-list {
          margin-top: 10px;
          padding-left: 18px;
          color: rgba(40,40,40,0.82);
          font-size: 13px;
        }

        .catalog-action {
          font-size: 13px;
          cursor: pointer;
          color: rgba(40,40,40,0.75);
          transition: color 0.15s ease;
          user-select: none;
        }

        .catalog-action:hover {
          color: #6a1e2d;
        }

        .catalog-action-danger:hover {
          color: #b4232c;
        }

        .catalog-action-disabled {
          opacity: 0.45;
          cursor: default;
          pointer-events: none;
        }

        .catalog-action-sep {
          margin: 0 6px;
          opacity: 0.4;
        }

        .catalog-toolbar-action {
          font-size: 14px;
          cursor: pointer;
          color: rgba(40,40,40,0.75);
          transition: color 0.15s ease;
          user-select: none;
        }

        .catalog-toolbar-action:hover {
          color: #6a1e2d;
        }

        .catalog-toolbar-quiet {
          font-size: 13px;
          color: rgba(40,40,40,0.58);
        }

        @media (max-width: 1400px) {
  .merchant-catalog-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

        @media (max-width: 1100px) {
          .merchant-catalog-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .merchant-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .merchant-readiness-overview {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 800px) {
          .merchant-catalog-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .merchant-summary-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 520px) {
          .merchant-catalog-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="merchant-readiness-overview">
        <div className="public-card">
          <div className="public-card-title">Catalog readiness</div>
          <div className="merchant-readiness-copy">
            <div className="public-card-sub">
              This is now your single working view for imports, readiness, visibility, and try-on setup.
            </div>
            <div className="public-card-sub">
              Use the filters below to focus on products that need fixing, then publish when they’re ready.
            </div>
          </div>
        </div>

        <div className="public-card">
          <div className="public-card-title">Current state</div>
          <div className="merchant-readiness-metrics">
            
            <div className="merchant-readiness-row">
              <span>Needs attention</span>
              <strong>{summary.needsAttention}</strong>
            </div>
            <div className="merchant-readiness-row">
              <span>Available for try on</span>
              <strong>{summary.tryOnAvailableCount}</strong>
            </div>
            <div className="merchant-readiness-row">
              <span>Published</span>
              <strong>{summary.publicCount}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="merchant-catalog-toolbar">
        <span
          className="catalog-toolbar-action"
          onClick={() => navigate("/merchant/dashboard/import-csv")}
        >
          Import products
        </span>

        <span className="catalog-action-sep">•</span>

        <span
          className="catalog-toolbar-action"
          onClick={() => navigate("/merchant/dashboard")}
        >
          Back to dashboard
        </span>

        <span className="catalog-action-sep">•</span>

        <span
          className="catalog-toolbar-action"
          onClick={() => setShowOnlyNeedsAttention((prev) => !prev)}
        >
          {showOnlyNeedsAttention ? "Show all products" : "Show needs attention only"}
        </span>

        <span className="catalog-action-sep">•</span>

        <span className="catalog-toolbar-action" onClick={toggleSelectAll}>
          {allSelected ? "Clear selection" : "Select all visible"}
        </span>

        {selectedIds.length > 0 ? (
          <>
            <span className="catalog-action-sep">•</span>

            <span
              className={`catalog-toolbar-action ${
                bulkPublishing ? "catalog-action-disabled" : ""
              }`}
              onClick={
                bulkPublishing || selectedInactiveCount === 0
                  ? undefined
                  : () => handleBulkSetActive(true)
              }
              title={
                selectedInactiveCount === 0
                  ? "All selected products are already public"
                  : "Publish selected products"
              }
            >
              {bulkPublishing
                ? "Publishing..."
                : `Publish selected${selectedInactiveCount ? ` (${selectedInactiveCount})` : ""}`}
            </span>

            <span className="catalog-action-sep">•</span>

            <span
              className={`catalog-toolbar-action ${
                bulkUnpublishing ? "catalog-action-disabled" : ""
              }`}
              onClick={
                bulkUnpublishing || selectedActiveCount === 0
                  ? undefined
                  : () => handleBulkSetActive(false)
              }
              title={
                selectedActiveCount === 0
                  ? "All selected products are already hidden"
                  : "Unpublish selected products"
              }
            >
              {bulkUnpublishing
                ? "Unpublishing..."
                : `Unpublish selected${selectedActiveCount ? ` (${selectedActiveCount})` : ""}`}
            </span>

            <span className="catalog-action-sep">•</span>

            <span
              className={`catalog-toolbar-action catalog-action-danger ${
                bulkDeleting ? "catalog-action-disabled" : ""
              }`}
              onClick={bulkDeleting ? undefined : handleBulkDelete}
            >
              {bulkDeleting
                ? "Deleting selected..."
                : `Delete selected (${selectedIds.length})`}
            </span>
          </>
        ) : (
          <>
            <span className="catalog-action-sep">•</span>
            <span className="catalog-toolbar-quiet">No products selected</span>
          </>
        )}
      </div>

      {loading ? (
        <div className="public-card">
          <div className="public-card-title">Loading catalog</div>
        </div>
      ) : !products.length ? (
        <div className="public-card">
          <div className="public-card-title">No products</div>
          <div className="public-card-sub">
            Import products to begin building your boutique.
          </div>
        </div>
      ) : !filteredProducts.length ? (
        <div className="public-card">
          <div className="public-card-title">No matching products</div>
          <div className="public-card-sub">
            There are currently no products in this view.
          </div>
        </div>
      ) : (
        <div className="merchant-catalog-grid">
          {filteredProducts.map((product) => {
            const tryOnState = getTryOnState(product);
            const debug = debugById[product.id] || null;
            const imageUrl = getProductImage(product);
            const selected = selectedIds.includes(product.id);
            const issues = getProductIssues(product);
            const tryOnAvailability = getTryOnAvailability(product);

            return (
              <div
                key={product.id}
                className="public-card merchant-product-card"
              >
                <div className="merchant-product-thumb-wrap">
                  <input
                    className="merchant-product-select"
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleSelected(product.id)}
                  />

                  <div
                    className={`merchant-product-status-badge ${
                      product.is_active ? "published" : "hidden"
                    }`}
                  >
                    {product.is_active ? "Published" : "Hidden"}
                  </div>

                  {imageUrl ? (
                    <img
  className="merchant-product-thumb"
  src={optimizedImageUrl(imageUrl, { width: 360, quality: 76 })}
  alt={product.title || "Product"}
  loading="lazy"
  decoding="async"
/>
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 13,
                        opacity: 0.65,
                      }}
                    >
                      No image
                    </div>
                  )}
                </div>

                <div className="merchant-product-meta">
                  <div className="public-card-title">
                    {product.title || "Untitled product"}
                  </div>

                  <div className="public-card-sub">
                    {product.category || "No category"} • {product.price_amount || "No price"}
                  </div>

                  <div className="merchant-status-block">
                    <div className="merchant-status-row">
                      <span className="merchant-status-label">Visibility</span>
                      <span className="merchant-status-value">
                        {product.is_active ? "Published" : "Hidden"}
                      </span>
                    </div>

                    <div className="merchant-status-row">
                      <span className="merchant-status-label">Try-on</span>
                      <span
                        className={`merchant-status-value ${
                          tryOnAvailability.tone === "available" ? "available" : "unavailable"
                        }`}
                      >
                        {tryOnAvailability.label}
                      </span>
                    </div>
                  </div>

                  {(() => {
                    const tryOn = product?.try_on || {};
                    const category = normalizeBrowseCategory(product?.category);

                    const inferredVariant = tryOn?.inferred_variant || "";
                    const inferredFit = tryOn?.inferred_fit || "";

                    const variantOptions = VARIANT_OPTIONS_BY_CATEGORY[category] || [];
                    const fitOptions = FIT_OPTIONS_BY_CATEGORY[category] || [];

                    const hasVariant = variantOptions.length > 0;
                    const hasFit = fitOptions.length > 0;

                    return (
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        {hasVariant && (
                          <div className="merchant-status-row">
                            <span className="merchant-status-label">Length</span>

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 2,
                              }}
                            >
                              <span style={{ fontSize: 11, opacity: 0.5 }}>
                                auto: {inferredVariant || "—"}
                              </span>

                              <select
                                value={tryOn?.override_variant ?? ""}
                                onChange={(e) =>
                                  handleOverrideChange(product.id, {
                                    override_variant: e.target.value || null,
                                  })
                                }
                                style={{
                                  fontSize: 12,
                                  borderRadius: 8,
                                  padding: "4px 8px",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  background: "white",
                                }}
                              >
                                <option value="">Auto (use system)</option>
                                {variantOptions.map((v) => (
                                  <option key={v} value={v}>
                                    {v}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {hasFit && (
                          <div className="merchant-status-row">
                            <span className="merchant-status-label">Fit</span>

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 2,
                              }}
                            >
                              <span style={{ fontSize: 11, opacity: 0.5 }}>
                                auto: {inferredFit || "—"}
                              </span>

                              <select
                                value={tryOn?.override_fit ?? ""}
                                onChange={(e) =>
                                  handleOverrideChange(product.id, {
                                    override_fit: e.target.value || null,
                                  })
                                }
                                style={{
                                  fontSize: 12,
                                  borderRadius: 8,
                                  padding: "4px 8px",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  background: "white",
                                }}
                              >
                                <option value="">Auto (use system)</option>
                                {fitOptions.map((f) => (
                                  <option key={f} value={f}>
                                    {f}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <details className="merchant-readiness-summary">
                    <summary className="merchant-readiness-summary-title">
                      <span>
                        {issues.length
                          ? `${issues.length} item${issues.length === 1 ? "" : "s"} needing attention`
                          : "Ready for launch preview"}
                      </span>
                      <span style={{ opacity: 0.58, fontWeight: 500 }}>
                        Review
                      </span>
                    </summary>

                    <div className="merchant-readiness-list">
                      {issues.length ? (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {issues.map((issue) => (
                            <li key={issue}>{issue}</li>
                          ))}
                        </ul>
                      ) : (
                        <div>No core catalog issues detected.</div>
                      )}

                      <div style={{ marginTop: 10 }}>
                        Try-on status: {tryOnState}
                      </div>
                    </div>
                  </details>
                </div>

                <div className="merchant-product-actions">
                  <span
                    className={`catalog-action ${
                      loadingDebugId === product.id ? "catalog-action-disabled" : ""
                    }`}
                    onClick={
                      loadingDebugId === product.id
                        ? undefined
                        : () => toggleDebug(product.id)
                    }
                  >
                    {loadingDebugId === product.id
                      ? "Loading..."
                      : debug
                      ? "Hide debug"
                      : "Debug"}
                  </span>

                  <span className="catalog-action-sep">•</span>

<span
  className={`catalog-action ${
    recomputingId === product.id ? "catalog-action-disabled" : ""
  }`}
  onClick={
    recomputingId === product.id
      ? undefined
      : () => setPendingRecomputeId(product.id)
  }
>
  {recomputingId === product.id ? "Recomputing..." : "Recompute mask"}
</span>

                  <span className="catalog-action-sep">•</span>

                  <span
                    className={`catalog-action ${
                      togglingActiveId === product.id ? "catalog-action-disabled" : ""
                    }`}
                    onClick={
                      togglingActiveId === product.id
                        ? undefined
                        : () => handleToggleActive(product)
                    }
                  >
                    {togglingActiveId === product.id
                      ? "Saving..."
                      : product.is_active
                      ? "Unpublish"
                      : "Publish"}
                  </span>

                  <span className="catalog-action-sep">•</span>

                  <span
                    className={`catalog-action catalog-action-danger ${
                      deleting === product.id ? "catalog-action-disabled" : ""
                    }`}
                    onClick={
                      deleting === product.id
                        ? undefined
                        : () => handleDelete(product.id)
                    }
                  >
                    {deleting === product.id ? "Deleting..." : "Delete"}
                  </span>
                </div>

                {debug ? (
                  <div className="merchant-product-debug">
                    <div className="public-card-title" style={{ marginBottom: 8 }}>
                      Try-on debug
                    </div>

                    <div className="public-card-sub">
                      status: {debug?.summary?.status || "missing"} • selection:{" "}
                      {debug?.summary?.selection_status || "missing"}
                    </div>

                    <div className="public-card-sub" style={{ marginTop: 8 }}>
                      inferred variant: {debug?.try_on?.inferred_variant || "—"} • override:{" "}
                      {debug?.try_on?.override_variant || "—"}
                    </div>

                    <div className="public-card-sub">
                      inferred fit: {debug?.try_on?.inferred_fit || "—"} • override:{" "}
                      {debug?.try_on?.override_fit || "—"}
                    </div>

                    <div className="public-card-sub" style={{ marginTop: 8 }}>
                      selected front: {debug?.summary?.selected_front || "none"}
                    </div>

                    <div className="public-card-sub">
                      selected back: {debug?.summary?.selected_back || "none"}
                    </div>

                    <div className="public-card-sub" style={{ marginTop: 8 }}>
                      front asset: {debug?.summary?.front_asset || "none"}
                    </div>

                    <div className="public-card-sub">
                      back asset: {debug?.summary?.back_asset || "none"}
                    </div>

                    <div className="public-card-sub" style={{ marginTop: 8 }}>
                      merchant reasons:{" "}
                      {debug?.summary?.merchant_reasons?.length
                        ? debug.summary.merchant_reasons.join(" • ")
                        : "none"}
                    </div>

                    <details style={{ marginTop: 10 }}>
                      <summary className="public-card-sub" style={{ cursor: "pointer" }}>
                        View image analysis ({debug?.analysis_debug?.length || 0})
                      </summary>
                      <pre
                        style={{
                          marginTop: 10,
                          padding: 12,
                          borderRadius: 10,
                          background: "rgba(0,0,0,0.06)",
                          overflowX: "auto",
                          whiteSpace: "pre-wrap",
                          fontSize: 12,
                        }}
                      >
                        {JSON.stringify(debug?.analysis_debug || [], null, 2)}
                      </pre>
                    </details>

                    <details style={{ marginTop: 10 }}>
                      <summary className="public-card-sub" style={{ cursor: "pointer" }}>
                        View raw try_on
                      </summary>
                      <pre
                        style={{
                          marginTop: 10,
                          padding: 12,
                          borderRadius: 10,
                          background: "rgba(0,0,0,0.06)",
                          overflowX: "auto",
                          whiteSpace: "pre-wrap",
                          fontSize: 12,
                        }}
                      >
                        {JSON.stringify(debug?.try_on || {}, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {pendingRecomputeId ? (
  <div className="veya-modal-backdrop">
    <div className="veya-modal">
      <div className="veya-modal-title">Recompute try-on mask?</div>

      <div className="veya-modal-copy">
        This will clear the current generated try-on asset and rerun segmentation
        from the product image. Use this after replacing or updating product imagery.
      </div>

      <div className="veya-modal-actions">
        <button
          type="button"
          className="veya-modal-button secondary"
          onClick={() => setPendingRecomputeId(null)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="veya-modal-button primary"
          onClick={() => handleRecompute(pendingRecomputeId)}
          disabled={recomputingId === pendingRecomputeId}
        >
          {recomputingId === pendingRecomputeId ? "Recomputing..." : "Recompute mask"}
        </button>
      </div>
    </div>
  </div>
) : null}
    </MerchantPageShell>
  );
}